// src/pages/finance-portal/tax-filings/TaxFilings.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, MenuItem,
    InputAdornment, IconButton, Tooltip, Grid,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    PENDING: { bg: '#fef3c7', color: '#92400e' },
    DUE: { bg: '#fecaca', color: '#991b1b' },
    FILED: { bg: '#dcfce7', color: '#166534' },
    LATE: { bg: '#fecaca', color: '#991b1b' },
    AMENDED: { bg: '#e0e7ff', color: '#3730a3' },
};

const FILING_TYPES = [
    { value: '', label: 'All Types' },
    { value: '941', label: 'Form 941 (Quarterly)' },
    { value: '940', label: 'Form 940 (Annual FUTA)' },
    { value: 'W-3', label: 'Form W-3 (W-2 Transmittal)' },
    { value: '1096', label: 'Form 1096 (1099 Transmittal)' },
    { value: 'STATE_WITHHOLDING', label: 'State Withholding' },
    { value: 'STATE_UNEMPLOYMENT', label: 'State Unemployment' },
    { value: 'OTHER', label: 'Other' },
];

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

export default function TaxFilings() {
    const [filings, setFilings] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Filters
    const [filingType, setFilingType] = useState('');
    const [taxYear, setTaxYear] = useState('');
    const [quarter, setQuarter] = useState('');
    const [status, setStatus] = useState('');

    // Add Filing Dialog
    const [formOpen, setFormOpen] = useState(false);
    const [formData, setFormData] = useState({
        filing_type: '941', tax_year: new Date().getFullYear(), quarter: '',
        period_start: '', period_end: '', due_date: '',
        total_wages: '', total_tax: '', notes: '',
    });
    const [saving, setSaving] = useState(false);

    const fetchFilings = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (filingType) params.filing_type = filingType;
            if (taxYear) params.tax_year = taxYear;
            if (quarter) params.quarter = quarter;
            if (status) params.status = status;
            const res = await axiosInstance.get('/api/finance/payroll/tax-filings', { params });
            if (res.data.success) {
                setFilings(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load tax filings');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, filingType, taxYear, quarter, status]);

    useEffect(() => { fetchFilings(); }, [fetchFilings]);

    const handleAddFiling = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                ...formData,
                tax_year: parseInt(formData.tax_year, 10),
                quarter: formData.quarter ? parseInt(formData.quarter, 10) : null,
                total_wages: parseFloat(formData.total_wages) || 0,
                total_tax: parseFloat(formData.total_tax) || 0,
            };
            await axiosInstance.post('/api/finance/payroll/tax-filings', payload);
            setFormOpen(false);
            setSuccess('Filing added');
            fetchFilings();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add filing');
        } finally {
            setSaving(false);
        }
    };

    const handleMarkFiled = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/payroll/tax-filings/${id}/mark-filed`);
            setSuccess('Filing marked as filed');
            fetchFilings();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to mark as filed');
        }
    };

    const openForm = () => {
        setFormData({
            filing_type: '941', tax_year: new Date().getFullYear(), quarter: '',
            period_start: '', period_end: '', due_date: '',
            total_wages: '', total_tax: '', notes: '',
        });
        setFormOpen(true);
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <GavelIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Tax Filings</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Track quarterly and annual tax filings</Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openForm}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'white', color: ACCENT,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        }}
                    >
                        Add Filing
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small" select label="Filing Type" value={filingType}
                        onChange={e => { setFilingType(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 180 }}
                    >
                        {FILING_TYPES.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                    </TextField>
                    <TextField
                        size="small" label="Tax Year" type="number" value={taxYear}
                        onChange={e => { setTaxYear(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        sx={{ minWidth: 110 }}
                    />
                    <TextField
                        size="small" select label="Quarter" value={quarter}
                        onChange={e => { setQuarter(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 110 }}
                    >
                        <MenuItem value="" sx={{ fontFamily }}>All</MenuItem>
                        {[1, 2, 3, 4].map(q => <MenuItem key={q} value={q} sx={{ fontFamily }}>Q{q}</MenuItem>)}
                    </TextField>
                    <TextField
                        size="small" select label="Status" value={status}
                        onChange={e => { setStatus(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 130 }}
                    >
                        <MenuItem value="" sx={{ fontFamily }}>All</MenuItem>
                        {Object.keys(STATUS_COLORS).map(s => (
                            <MenuItem key={s} value={s} sx={{ fontFamily }}>{getStatusLabel(s)}</MenuItem>
                        ))}
                    </TextField>
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Filing Type', 'Year', 'Quarter', 'Period', 'Due Date', 'Filed Date', 'Wages', 'Tax', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                    ))
                                ) : filings.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No tax filings found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : filings.map(f => (
                                    <TableRow key={f.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{f.filing_type}</TableCell>
                                        <TableCell sx={bodyCellSx}>{f.tax_year}</TableCell>
                                        <TableCell sx={bodyCellSx}>{f.quarter ? `Q${f.quarter}` : '—'}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {f.period_start && f.period_end ? `${formatDate(f.period_start)} – ${formatDate(f.period_end)}` : '—'}
                                        </TableCell>
                                        <TableCell sx={{ ...bodyCellSx, color: f.status === 'DUE' || f.status === 'LATE' ? '#dc2626' : '#1e293b' }}>
                                            {formatDate(f.due_date)}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{formatDate(f.filed_date)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{formatCurrency(f.total_wages)}</TableCell>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{formatCurrency(f.total_tax)}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Chip label={getStatusLabel(f.status)} size="small"
                                                sx={{
                                                    fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                    bgcolor: (STATUS_COLORS[f.status] || STATUS_COLORS.PENDING).bg,
                                                    color: (STATUS_COLORS[f.status] || STATUS_COLORS.PENDING).color,
                                                }} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {f.status !== 'FILED' && (
                                                <Tooltip title="Mark as Filed">
                                                    <IconButton size="small" onClick={() => handleMarkFiled(f.id)}>
                                                        <CheckCircleIcon sx={{ fontSize: 18, color: ACCENT }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 20, 50]}
                        sx={{ '& *': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Add Filing Dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Add Tax Filing</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="Filing Type" required
                                value={formData.filing_type}
                                onChange={e => setFormData(prev => ({ ...prev, filing_type: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                {FILING_TYPES.filter(t => t.value).map(t => (
                                    <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={3}>
                            <TextField size="small" fullWidth label="Tax Year" type="number" required
                                value={formData.tax_year}
                                onChange={e => setFormData(prev => ({ ...prev, tax_year: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField size="small" fullWidth select label="Quarter"
                                value={formData.quarter}
                                onChange={e => setFormData(prev => ({ ...prev, quarter: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                <MenuItem value="" sx={{ fontFamily }}>N/A</MenuItem>
                                {[1, 2, 3, 4].map(q => <MenuItem key={q} value={q} sx={{ fontFamily }}>Q{q}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Period Start" type="date"
                                value={formData.period_start}
                                onChange={e => setFormData(prev => ({ ...prev, period_start: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Period End" type="date"
                                value={formData.period_end}
                                onChange={e => setFormData(prev => ({ ...prev, period_end: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Due Date" type="date"
                                value={formData.due_date}
                                onChange={e => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField size="small" fullWidth label="Total Wages" type="number"
                                value={formData.total_wages}
                                onChange={e => setFormData(prev => ({ ...prev, total_wages: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField size="small" fullWidth label="Total Tax" type="number"
                                value={formData.total_tax}
                                onChange={e => setFormData(prev => ({ ...prev, total_tax: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Notes" multiline rows={2}
                                value={formData.notes}
                                onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFormOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleAddFiling} variant="contained" disabled={saving || !formData.filing_type}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
